import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Dropdown,
  Form,
  Modal,
  OverlayTrigger,
  Table,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AnomalyPeriodSelector from "./AnomalyPeriodSelector";
import { ScaleLoader } from "react-spinners";
import { FcFinePrint } from "react-icons/fc";
import { BsFiletypePdf, BsFiletypeXlsx, BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import moment from "moment";
import fileDownload from "js-file-download";
import { RegulationContext } from "../../../App";
import AnomalyService from "../../../core/service/AnomalyService";
import CompanyService from "../../../core/service/CompanyService";
import ANOMALY_RULE_CODES, { resolveAnomalyRuleLabel } from "../../../core/constants/anomalyRuleCodes";
import { ANOMALY_SEVERITY_BADGE, formatAnomalySeverityLabel, formatAnomalyStatusLabel } from "../../../core/constants/anomalySeverity";
import Pagination from "../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../core/constants/common";
import Utils from "../../../core/utilities/Utils";
import AnomalyInfoTip from "./AnomalyInfoTip";
import { useAnomalyDetection } from "./AnomalyDetectionContext";
import {
  ANOMALY_DATEPICKER_FORMAT,
  formatAnomalyApiDate,
  formatAnomalyDisplayDate,
  formatAnomalyDisplayDateTime,
  formatAnomalyMonthYear,
  formatAnomalyRecordDisplayPeriod,
  getAnomalyDisplayPeriod,
  getAnomalyMaxSelectableDate,
  getAnomalyDefaultListPeriodRange,
  getAnomalyDefaultResolvedPeriodRange,
  getAnomalyPeriodFromMaxDate,
  getAnomalyResolvedDateMaxSelectableDate,
  getAnomalyResolvedPeriodFromMaxDate,
  normalizeAnomalyPeriodRange,
  normalizeAnomalyResolvedPeriodRange,
  onAnomalyPeriodChange,
  onAnomalyResolvedPeriodChange,
  parseAnomalyPeriodDate,
  parseAnomalyResolvedPeriodDate,
} from "./anomalyDateUtils";
import { buildListTimeSeriesNavState } from "./anomalyNavUtils";
import { resolveAnomalyRegulationId } from "./anomalyRegulationUtils";
import { canShowFixAction } from "./anomalyFixUtils";
import AnomalyFixModal from "./AnomalyFixModal";
import AnomalyDetailModal from "./AnomalyDetailModal";
import "./AnomalyList.scss";

const STATUS_BADGE = {
  OPEN: "badge-Critical",
  DISMISSED: "badge-Dismissed",
  RESOLVED: "badge-Closed",
};

const DISMISS_TYPES = ["PERMANENT", "UNTIL_DATE", "UNTIL_VALUE_CHANGES"];

const formatDismissTypeLabel = (type) =>
  type.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

const LIST_VIEW_ACTIVE = "active";
const LIST_VIEW_HISTORY = "history";

const LIST_STORAGE_KEY = "anomaly_list_filters";

const ANOMALY_SEARCH_MIN_LENGTH = 2;

// Backend ignores keys shorter than 2 chars; don't send them at all
const getEffectiveSearchKey = (value) => {
  const trimmed = (value || "").trim();
  return trimmed.length >= ANOMALY_SEARCH_MIN_LENGTH ? trimmed : "";
};

const FILTER_NAV_STATE_KEYS = [
  "regulationId",
  "metricId",
  "branchId",
  "branchName",
  "status",
  "severity",
  "ruleCode",
  "periodFrom",
  "periodTo",
];

const readListStorage = () => {
  try { return JSON.parse(sessionStorage.getItem(LIST_STORAGE_KEY) || "{}"); }
  catch { return {}; }
};

const buildStoragePayload = ({
  status = "",
  severity = "",
  ruleCodes = [],
  branchId = "",
  branchName = "",
  periodFrom = null,
  periodTo = null,
  listViewMode = LIST_VIEW_ACTIVE,
  resolvedBy = "",
  searchKey = "",
} = {}) => ({
  status,
  severity,
  ruleCodes,
  branchId,
  branchName,
  periodFrom: periodFrom ? periodFrom.toISOString() : null,
  periodTo: periodTo ? periodTo.toISOString() : null,
  listViewMode,
  resolvedBy,
  searchKey,
});

const writeListStorage = (payload) => {
  sessionStorage.setItem(LIST_STORAGE_KEY, JSON.stringify(payload));
};

const CHILD_BRANCH_LEVEL = 2;

const sortChildBranches = (units = []) =>
  [...units].sort((a, b) => {
    const parentOrder = (a.parent?.sortOrder ?? 0) - (b.parent?.sortOrder ?? 0);
    if (parentOrder !== 0) return parentOrder;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });

const formatChildBranchOptionLabel = (unit) => unit?.name || "—";

const getDismissalSuggestions = (ruleCode) => {
  switch (ruleCode) {
    case "SPIKE_RULE": return ["Verified Correct Increase", "Seasonal Spike", "Known External Event", "Data Entry Error"];
    case "DROP_RULE": return ["Verified Correct Decrease", "Temporary Dip", "Known External Event", "Data Entry Error"];
    case "ZERO_AFTER_NON_ZERO_RULE": return ["Expected Zero", "No Activity This Period", "Data Entry Error"];
    case "MISSING_PERIOD_GAP_RULE": return ["Data Unavailable", "Not Required This Period"];
    case "NEGATIVE_VALUE_RULE": return ["Credit/Refund Applied", "Verified Correct Negative", "Data Entry Error"];
    case "CONSECUTIVE_IDENTICAL_RULE": return ["Expected Constant Value", "No Change in Activity", "Data Entry Error"];
    default: return ["Seasonal Variance", "Known External Event", "Acceptable Outlier", "Data Entry Error"];
  }
};

const AnomalyList = ({ t }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state || {};
  const { glbRegulation } = useContext(RegulationContext);
  const { lastCompletedAt } = useAnomalyDetection();
  const apiRegulationId = resolveAnomalyRegulationId(glbRegulation);

  // navState (from dashboard) wins on drill-down; sessionStorage wins after banner clears / reload
  const saved = readListStorage();
  const initHistoryView =
    saved.listViewMode === LIST_VIEW_HISTORY ||
    navState.status === "RESOLVED";

  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const [metricId, setMetricId] = useState(navState.metricId ? String(navState.metricId) : "");
  const [branchId, setBranchId] = useState(() => {
    if (navState.branchId) return navState.branchId;
    if ("branchId" in saved) return saved.branchId || "";
    return "";
  });
  const [branchName, setBranchName] = useState(() => {
    if (navState.branchName) return navState.branchName;
    if ("branchName" in saved) return saved.branchName || "";
    return "";
  });
  const [childBranches, setChildBranches] = useState([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [status, setStatus] = useState(() => {
    if (initHistoryView) return "RESOLVED";
    return navState.status || saved.status || "";
  });
  const statusBeforeHistoryRef = useRef(
    navState.status && navState.status !== "RESOLVED"
      ? navState.status
      : saved.status || ""
  );
  const [listViewMode, setListViewMode] = useState(
    initHistoryView ? LIST_VIEW_HISTORY : LIST_VIEW_ACTIVE
  );
  const [resolvedBy, setResolvedBy] = useState(saved.resolvedBy || "");
  const [searchKey, setSearchKey] = useState(saved.searchKey || "");
  const [severity, setSeverity] = useState(() => {
    if (navState.severity) return navState.severity;
    if ("severity" in saved) return saved.severity || "";
    return "";
  });
  const [ruleCodes, setRuleCodes] = useState(() => {
    if (navState.ruleCode) return [navState.ruleCode];
    if ("ruleCodes" in saved) return saved.ruleCodes || [];
    return [];
  });
  const [ruleDropOpen, setRuleDropOpen] = useState(false);
  const ruleDropRef = useRef(null);
  const parseInitialPeriodDate = (value) =>
    initHistoryView ? parseAnomalyResolvedPeriodDate(value) : parseAnomalyPeriodDate(value);

  const resolvedDefaultPeriodRange = getAnomalyDefaultResolvedPeriodRange();

  const initialListPeriods = initHistoryView
    ? normalizeAnomalyResolvedPeriodRange(
      navState.periodFrom ? parseAnomalyResolvedPeriodDate(navState.periodFrom)
        : saved.periodFrom ? parseAnomalyResolvedPeriodDate(saved.periodFrom)
          : resolvedDefaultPeriodRange.periodFrom,
      navState.periodTo ? parseAnomalyResolvedPeriodDate(navState.periodTo)
        : saved.periodTo ? parseAnomalyResolvedPeriodDate(saved.periodTo)
          : resolvedDefaultPeriodRange.periodTo
    )
    : normalizeAnomalyPeriodRange(
      navState.periodFrom ? parseAnomalyPeriodDate(navState.periodFrom)
        : saved.periodFrom ? parseAnomalyPeriodDate(saved.periodFrom) : null,
      navState.periodTo ? parseAnomalyPeriodDate(navState.periodTo)
        : saved.periodTo ? parseAnomalyPeriodDate(saved.periodTo) : null
    );
  const [periodFrom, setPeriodFrom] = useState(initialListPeriods.periodFrom);
  const [periodTo, setPeriodTo] = useState(initialListPeriods.periodTo);

  const isHistoryView = listViewMode === LIST_VIEW_HISTORY;
  const periodMaxDate = isHistoryView
    ? getAnomalyResolvedDateMaxSelectableDate()
    : getAnomalyMaxSelectableDate();

  const [showDismiss, setShowDismiss] = useState(false);
  const [dismissRecord, setDismissRecord] = useState(null);
  const [dismissType, setDismissType] = useState("PERMANENT");
  const [suppressDate, setSuppressDate] = useState(null);
  const [suppressValue, setSuppressValue] = useState("");
  const [dismissReason, setDismissReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showDetail, setShowDetail] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showFix, setShowFix] = useState(false);
  const [fixRecordId, setFixRecordId] = useState(null);

  const tableColSpan = 8;

  // Close rule-type dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (ruleDropRef.current && !ruleDropRef.current.contains(e.target)) {
        setRuleDropOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Persist core filters to sessionStorage so they survive page navigation
  useEffect(() => {
    writeListStorage(buildStoragePayload({
      status,
      severity,
      ruleCodes,
      branchId,
      branchName,
      periodFrom,
      periodTo,
      listViewMode,
      resolvedBy,
      searchKey,
    }));
  }, [status, severity, ruleCodes, branchId, branchName, periodFrom, periodTo, listViewMode, resolvedBy, searchKey]);

  const persistListFilters = (overrides = {}) => {
    writeListStorage(buildStoragePayload({
      status,
      severity,
      ruleCodes,
      branchId,
      branchName,
      periodFrom,
      periodTo,
      listViewMode,
      resolvedBy,
      searchKey,
      ...overrides,
    }));
  };

  const stripNavState = (keys) => {
    const next = { ...(location.state || {}) };
    keys.forEach((key) => { delete next[key]; });
    navigate(location.pathname, { replace: true, state: next });
  };

  const showBackNav = Boolean(navState.fromDashboard) || FILTER_NAV_STATE_KEYS.some((key) => navState[key]);

  const buildResetNavState = () => {
    const cameFromDashboard =
      Boolean(navState.fromDashboard) ||
      FILTER_NAV_STATE_KEYS.some((key) => navState[key]);
    return cameFromDashboard ? { fromDashboard: true } : {};
  };

  const buildFilterSnapshot = (overrides = {}) => {
    const viewMode = overrides.listViewMode ?? listViewMode;
    const s = {
      metricId, branchId, status, severity, ruleCodes,
      periodFrom, periodTo, resolvedBy, searchKey,
      ...overrides,
    };
    const params = { regulationId: apiRegulationId };
    if (s.metricId) params.metricId = s.metricId;
    if (s.branchId) params.branchId = s.branchId;
    if (s.datapointId) params.datapointId = s.datapointId;
    const effectiveSearchKey = getEffectiveSearchKey(s.searchKey);
    if (effectiveSearchKey) params.key = effectiveSearchKey;
    if (viewMode === LIST_VIEW_HISTORY) {
      params.status = "RESOLVED";
      if (s.resolvedBy?.trim()) params.resolvedBy = s.resolvedBy.trim();
    } else if (s.status) {
      params.status = s.status;
    }
    if (s.severity) params.severity = s.severity;
    if (s.ruleCodes?.length) params.ruleCodes = s.ruleCodes;
    if (s.periodFrom) params.periodFrom = formatAnomalyApiDate(s.periodFrom);
    if (s.periodTo) params.periodTo = formatAnomalyApiDate(s.periodTo);
    return params;
  };

  const buildParams = (page, overrides = {}, size = itemsPerPage) => ({
    ...buildFilterSnapshot(overrides),
    page: page > 0 ? page - 1 : 0,
    size,
  });

  const showListError = () => {
    toast.error(t("AnomalyDetection.listError.label"), {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const fetchList = (page, filterOverrides = {}, options = {}) => {
    const size = options.pageSize ?? itemsPerPage;
    setIsLoading(true);
    AnomalyService.getList(buildParams(page, filterOverrides, size))
      .then((res) => {
        setList(res.data || []);
        setTotalCount(parseFloat(res.headers["x-total-count"] || 0));
      })
      .catch(showListError)
      .finally(() => setIsLoading(false));
  };

  const clearedBannerFilters = {
    metricId: "",
    branchId: "",
    branchName: "",
    status: "",
    severity: "",
    ruleCodes: [],
    periodFrom: null,
    periodTo: null,
    searchKey: "",
  };

  const getResetFilterOverrides = () => {
    const { periodFrom: defaultFrom, periodTo: defaultTo } = isHistoryView
      ? getAnomalyDefaultResolvedPeriodRange()
      : getAnomalyDefaultListPeriodRange();
    return {
      ...clearedBannerFilters,
      periodFrom: defaultFrom,
      periodTo: defaultTo,
    };
  };

  const handleRemoveBranchFilter = () => {
    setBranchId("");
    setBranchName("");
    setCurrentPage(1);
    persistListFilters({ branchId: "", branchName: "" });
    stripNavState(["branchId", "branchName"]);
    fetchList(1, { branchId: "", branchName: "" });
  };

  const handleRemoveRuleCode = (code) => {
    const nextRuleCodes = ruleCodes.filter((c) => c !== code);
    setRuleCodes(nextRuleCodes);
    setCurrentPage(1);
    persistListFilters({ ruleCodes: nextRuleCodes });
    if (nextRuleCodes.length === 0) stripNavState(["ruleCode"]);
    fetchList(1, { ruleCodes: nextRuleCodes });
  };

  const handleRemoveSeverityFilter = () => {
    setSeverity("");
    setCurrentPage(1);
    persistListFilters({ severity: "" });
    stripNavState(["severity"]);
    fetchList(1, { severity: "" });
  };

  useEffect(() => {
    setIsLoadingBranches(true);
    CompanyService.getEnterprisesByLevel(CHILD_BRANCH_LEVEL)
      .then((res) => {
        if (res.status !== 200) return;
        setChildBranches(sortChildBranches(res.data || []));
      })
      .catch(() => {
        setChildBranches([]);
      })
      .finally(() => setIsLoadingBranches(false));
  }, []);

  useEffect(() => {
    if (!branchId || branchName || childBranches.length === 0) return;
    const match = childBranches.find((unit) => String(unit.id) === String(branchId));
    if (match) setBranchName(match.name);
  }, [branchId, branchName, childBranches]);

  const handleBranchFilterChange = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setBranchId("");
      setBranchName("");
      return;
    }
    const match = childBranches.find((unit) => String(unit.id) === selectedId);
    setBranchId(selectedId);
    setBranchName(match?.name || "");
  };

  useEffect(() => {
    const initOverrides = {};
    if (initHistoryView) {
      initOverrides.listViewMode = LIST_VIEW_HISTORY;
      initOverrides.status = "RESOLVED";
    } else if (navState.status) {
      initOverrides.status = navState.status;
    }
    if (navState.severity) initOverrides.severity = navState.severity;
    if (navState.metricId) initOverrides.metricId = String(navState.metricId);
    if (navState.ruleCode) initOverrides.ruleCodes = [navState.ruleCode];
    if (navState.periodFrom) initOverrides.periodFrom = parseInitialPeriodDate(navState.periodFrom);
    if (navState.periodTo) initOverrides.periodTo = parseInitialPeriodDate(navState.periodTo);

    fetchList(1, initOverrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!lastCompletedAt) return;
    fetchList(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCompletedAt]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchList(1);
  };

  const handleReset = () => {
    const resetFilters = getResetFilterOverrides();
    setMetricId("");
    setBranchId("");
    setBranchName("");
    setSearchKey("");
    if (isHistoryView) {
      setResolvedBy("");
    } else {
      setStatus("");
    }
    setSeverity("");
    setRuleCodes([]);
    setPeriodFrom(resetFilters.periodFrom);
    setPeriodTo(resetFilters.periodTo);
    setCurrentPage(1);
    writeListStorage(buildStoragePayload({
      status: isHistoryView ? "RESOLVED" : "",
      severity: "",
      ruleCodes: [],
      branchId: "",
      branchName: "",
      periodFrom: resetFilters.periodFrom,
      periodTo: resetFilters.periodTo,
      listViewMode,
      resolvedBy: "",
    }));
    navigate(location.pathname, { replace: true, state: buildResetNavState() });
    fetchList(1, {
      ...resetFilters,
      status: isHistoryView ? "RESOLVED" : "",
      resolvedBy: "",
    });
  };

  const handleViewModeChange = (mode) => {
    if (mode === listViewMode) return;
    setListViewMode(mode);
    setCurrentPage(1);

    if (mode === LIST_VIEW_HISTORY) {
      statusBeforeHistoryRef.current = status;
      const { periodFrom: defaultFrom, periodTo: defaultTo } =
        getAnomalyDefaultResolvedPeriodRange();
      setStatus("RESOLVED");
      setPeriodFrom(defaultFrom);
      setPeriodTo(defaultTo);
      persistListFilters({
        listViewMode: mode,
        status: "RESOLVED",
        periodFrom: defaultFrom,
        periodTo: defaultTo,
      });
      fetchList(1, {
        listViewMode: mode,
        status: "RESOLVED",
        periodFrom: defaultFrom,
        periodTo: defaultTo,
      });
      return;
    }

    const restoredStatus = statusBeforeHistoryRef.current || "";
    const { periodFrom: defaultFrom, periodTo: defaultTo } =
      getAnomalyDefaultListPeriodRange();
    setStatus(restoredStatus);
    setResolvedBy("");
    setPeriodFrom(defaultFrom);
    setPeriodTo(defaultTo);
    persistListFilters({
      listViewMode: mode,
      status: restoredStatus,
      resolvedBy: "",
      periodFrom: defaultFrom,
      periodTo: defaultTo,
    });
    fetchList(1, {
      listViewMode: mode,
      status: restoredStatus,
      resolvedBy: "",
      periodFrom: defaultFrom,
      periodTo: defaultTo,
    });
  };

  const buildExportParams = () => buildFilterSnapshot();

  const getTimeSeriesNavState = (overrides = {}) =>
    buildListTimeSeriesNavState(
      { periodFrom, periodTo, branchId, branchName, status, severity, ruleCodes },
      overrides
    );

  const buildExportFilename = (ext = "pdf") => {
    const parts = ["anomaly-report"];
    if (isHistoryView) parts.push("resolved");
    else if (status) parts.push(status.toLowerCase());
    if (severity) parts.push(severity.toLowerCase());
    if (resolvedBy) parts.push(resolvedBy.replace(/[@.]/g, "_"));
    if (periodFrom) parts.push(moment(periodFrom).format("YYYY-MM-DD"));
    if (periodTo) parts.push(moment(periodTo).format("YYYY-MM-DD"));
    if (parts.length === 1) parts.push(moment().format("YYYY-MM-DD"));
    return `${parts.join("_")}.${ext}`;
  };

  const getFilenameFromDisposition = (headers, fallback) => {
    const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"];
    if (!cd) return fallback;
    const utf8Match = cd.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (utf8Match) {
      try { return decodeURIComponent(utf8Match[1]); } catch { /* fall through */ }
    }
    const plainMatch = cd.match(/filename="?([^";\s]+)"?/i);
    return plainMatch ? plainMatch[1] : fallback;
  };

  const buildExportTooltip = (exportLabel) =>
    [
      exportLabel,
      getEffectiveSearchKey(searchKey)
        ? `${t("AnomalyDetection.search.label")}: ${getEffectiveSearchKey(searchKey)}`
        : null,
      isHistoryView
        ? `Status: ${formatAnomalyStatusLabel("RESOLVED")}`
        : status ? `Status: ${formatAnomalyStatusLabel(status)}` : null,
      severity ? `Severity: ${formatAnomalySeverityLabel(severity)}` : null,
      resolvedBy ? `Resolved by: ${resolvedBy}` : null,
      periodFrom
        ? `${isHistoryView ? "Resolved from" : "From"}: ${formatAnomalyDisplayDate(periodFrom)}`
        : null,
      periodTo
        ? `${isHistoryView ? "Resolved to" : "To"}: ${formatAnomalyDisplayDate(periodTo)}`
        : null,
    ].filter(Boolean).join(" · ");

  const handleExportPdf = () => {
    setIsExportingPdf(true);
    AnomalyService.exportPdf(buildExportParams())
      .then((res) => {
        fileDownload(res.data, buildExportFilename("pdf"));
      })
      .catch(() => {
        toast.error(t("AnomalyDetection.exportError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setIsExportingPdf(false));
  };

  const handleExportExcel = () => {
    setIsExportingExcel(true);
    AnomalyService.exportExcel(buildExportParams())
      .then((res) => {
        const filename = getFilenameFromDisposition(
          res.headers,
          `anomaly-report-${moment().format("YYYY-MM-DD")}.xlsx`
        );
        fileDownload(res.data, filename);
      })
      .catch(() => {
        toast.error(t("AnomalyDetection.exportExcelError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setIsExportingExcel(false));
  };

  const openDismiss = (record) => {
    setDismissRecord(record);
    setDismissType("PERMANENT");
    setSuppressDate(null);
    setSuppressValue("");
    setDismissReason("");
    setShowDismiss(true);
  };

  const handleDismissSubmit = () => {
    if (!dismissRecord) return;
    if (dismissType === "UNTIL_DATE" && !suppressDate) {
      toast.error(t("AnomalyDetection.suppressUntilDateRequired.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    if (dismissType === "UNTIL_VALUE_CHANGES" && !suppressValue.trim()) {
      toast.error(t("AnomalyDetection.suppressUntilValueRequired.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    const payload = {
      anomalyRecordId: dismissRecord.id,
      dismissalType: dismissType,
      reason: dismissReason,
    };
    if (dismissType === "UNTIL_DATE" && suppressDate) {
      payload.suppressedUntilDate = formatAnomalyApiDate(suppressDate);
    }
    if (dismissType === "UNTIL_VALUE_CHANGES") {
      payload.suppressedUntilValue = suppressValue;
    }
    setIsSubmitting(true);
    AnomalyService.dismiss(payload)
      .then(() => {
        toast.success(t("AnomalyDetection.dismissSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        setShowDismiss(false);
        fetchList(currentPage);
      })
      .catch(() => {
        toast.error(t("AnomalyDetection.dismissChartError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleRevoke = (id) => {
    AnomalyService.revokeSuppression(id)
      .then(() => {
        toast.success(t("AnomalyDetection.reopenSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        fetchList(currentPage);
      })
      .catch(() => {
        toast.error(t("AnomalyDetection.reopenError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const openDetail = (record) => {
    setDetailRecord(null);
    setPreviewRecord(record);
    setShowDetail(true);
    setLoadingDetail(true);
    AnomalyService.getById(record.id)
      .then((res) => setDetailRecord(res.data))
      .catch(() => {
        toast.error(t("AnomalyDetection.detailError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setLoadingDetail(false));
  };

  const openFix = (record) => {
    setFixRecordId(record.id);
    setShowFix(true);
  };

  const handleFixSuccess = () => {
    fetchList(currentPage);
  };

  const navigateToTimeSeriesFromRecord = (record) => {
    const effectivePeriod = getAnomalyDisplayPeriod(record) || record.period;
    navigate("../time_series", {
      state: getTimeSeriesNavState({
        metricId: record.metricId,
        metricName: record.metricName,
        selectedPeriod: effectivePeriod,
        branchId: branchId || record.branchId,
        branchName: branchName || record.branchName,
        datapointId: record.datapointId,
        datapointName: record.datapointName,
        ruleCode: ruleCodes[0] || record.ruleCode || "",
      }),
    });
  };

  const renderSeverityBadge = (severity) => (
    <Badge className="rounded-pill" bg={ANOMALY_SEVERITY_BADGE[severity] || "secondary"}>
      {formatAnomalySeverityLabel(severity)}
    </Badge>
  );

  const renderStatusBadge = (status) => (
    <Badge className="rounded-pill" bg={STATUS_BADGE[status] || "secondary"}>
      {formatAnomalyStatusLabel(status)}
    </Badge>
  );

  const renderDeviationPct = (val) => {
    if (val == null) return "—";
    const cls = val >= 0 ? "deviation-positive" : "deviation-negative";
    return <span className={cls}>{val.toFixed(2)}%</span>;
  };

  const renderResolutionSummary = (item) => {
    const hasPrev = item.previousValue != null && item.previousValue !== "";
    const hasCorr = item.correctedValue != null && item.correctedValue !== "";
    if (!hasPrev && !hasCorr) return "—";

    return (
      <div className="resolution-summary">
        <div className="resolution-summary-values">
          {hasPrev && (
            <span className="resolution-summary-prev">{item.previousValue}</span>
          )}
          {hasPrev && hasCorr && (
            <span className="resolution-summary-arrow" aria-hidden="true">→</span>
          )}
          {hasCorr && (
            <span className="resolution-summary-corr">{item.correctedValue}</span>
          )}
          {item.fixUom && (
            <span className="resolution-summary-uom">{item.fixUom}</span>
          )}
        </div>
        {item.resolvedBy && (
          <span className="resolution-summary-by" title={item.resolvedBy}>
            {item.resolvedBy}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="anomaly-list-page">
      {/* Header */}
      <div className="List_header">
        <h5 className="MH">
          {showBackNav && (
            <span
              className="back-nav-btn anomaly-back-nav-btn"
              onClick={() => navigate(-1)}
              title={t("Back.label")}
            >
              &#8592;
            </span>
          )}
          {t("AnomalyList.label")}
        </h5>
        <ul className="List_sorts_menu">
          <li>
            <OverlayTrigger
              placement="top"
              overlay={Utils.renderTooltip(
                isExportingExcel
                  ? t("AnomalyDetection.exporting.label")
                  : buildExportTooltip(t("AnomalyDetection.exportExcelToolTip.label"))
              )}
            >
              <button
                className={`export-icon-btn excel-icon-btn${isExportingExcel ? " export-icon-btn--loading" : ""}`}
                onClick={handleExportExcel}
                disabled={isExportingExcel || isExportingPdf}
                aria-label={t("AnomalyDetection.exportExcel.label")}
              >
                {isExportingExcel
                  ? <ScaleLoader color="#fff" height={12} width={2} margin={1} />
                  : <BsFiletypeXlsx size={20} />
                }
              </button>
            </OverlayTrigger>
          </li>
          <li>
            <OverlayTrigger
              placement="top"
              overlay={Utils.renderTooltip(
                isExportingPdf
                  ? t("AnomalyDetection.exporting.label")
                  : buildExportTooltip(t("AnomalyDetection.exportPdfToolTip.label"))
              )}
            >
              <button
                className={`export-icon-btn pdf-icon-btn${isExportingPdf ? " export-icon-btn--loading" : ""}`}
                onClick={handleExportPdf}
                disabled={isExportingPdf || isExportingExcel}
                aria-label={t("AnomalyDetection.exportPdf.label")}
              >
                {isExportingPdf
                  ? <ScaleLoader color="#fff" height={12} width={2} margin={1} />
                  : <BsFiletypePdf size={20} />
                }
              </button>
            </OverlayTrigger>
          </li>
        </ul>
      </div>

      <div className="anomaly-list-view-toggle" data-testid="anomaly-list-view-toggle">
        <button
          type="button"
          className={`anomaly-list-view-toggle__btn${!isHistoryView ? " active" : ""}`}
          onClick={() => handleViewModeChange(LIST_VIEW_ACTIVE)}
          aria-pressed={!isHistoryView}
        >
          {t("AnomalyDetection.listViewActive.label")}
        </button>
        <button
          type="button"
          className={`anomaly-list-view-toggle__btn${isHistoryView ? " active" : ""}`}
          onClick={() => handleViewModeChange(LIST_VIEW_HISTORY)}
          aria-pressed={isHistoryView}
        >
          {t("AnomalyDetection.listViewHistory.label")}
        </button>
      </div>

      {/* Active filter chips */}
      {(severity || ruleCodes.length > 0 || branchId) && (
        <div className="nav-filter-chips">
          <span className="chips-label">{t("AnomalyDetection.filteredBy.label")}</span>
          {severity && (
            <span className="filter-chip removable-chip">
              {t("AnomalyDetection.severity.label")}: <strong>{formatAnomalySeverityLabel(severity)}</strong>
              <button
                className="chip-remove"
                onClick={handleRemoveSeverityFilter}
                aria-label={`Remove severity filter`}
              >✕</button>
            </span>
          )}
          {branchId && (
            <span className="filter-chip removable-chip">
              {t("AnomalyDetection.branch.label")}: <strong>{branchName || branchId}</strong>
              <button
                className="chip-remove"
                onClick={handleRemoveBranchFilter}
                aria-label={`Remove branch filter`}
              >✕</button>
            </span>
          )}
          {ruleCodes.map((code) => (
            <span key={code} className="filter-chip removable-chip">
              {t("AnomalyDetection.ruleType.label")}: <strong>{resolveAnomalyRuleLabel(t, code)}</strong>
              <button
                className="chip-remove"
                onClick={() => handleRemoveRuleCode(code)}
                aria-label={`Remove ${resolveAnomalyRuleLabel(t, code)}`}
              >✕</button>
            </span>
          ))}
          <button className="chip-clear" onClick={handleReset}>✕ {t("Reset.label")}</button>
        </div>
      )}

      {/* Filter Card */}
      <div className="anomaly-filter-card">
        <div className="filter-row">
          <div className="filter-group anomaly-search-group">
            <label className="label-with-tip">
              {t("AnomalyDetection.search.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.searchToolTip.label")} />
            </label>
            <Form.Control
              type="search"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              placeholder={t("AnomalyDetection.searchPlaceholder.label")}
              aria-label={t("AnomalyDetection.searchPlaceholder.label")}
            />
          </div>

          {!isHistoryView && (
            <div className="filter-group">
              <label className="label-with-tip">
                {t("AnomalyDetection.status.label")}
                <AnomalyInfoTip text={t("AnomalyDetection.statusFilterToolTip.label")} />
              </label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">{t("AnomalyDetection.statusAll.label")}</option>
                <option value="OPEN">{formatAnomalyStatusLabel("OPEN")}</option>
                <option value="DISMISSED">{formatAnomalyStatusLabel("DISMISSED")}</option>
              </Form.Select>
            </div>
          )}

          <div className="filter-group">
            <label className="label-with-tip">
              {t("AnomalyDetection.severity.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.severityToolTip.label")} />
            </label>
            <Form.Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">{t("AnomalyDetection.statusAll.label")}</option>
              <option value="CRITICAL">{formatAnomalySeverityLabel("CRITICAL")}</option>
              <option value="HIGH">{formatAnomalySeverityLabel("HIGH")}</option>
              <option value="MEDIUM">{formatAnomalySeverityLabel("MEDIUM")}</option>
              <option value="LOW">{formatAnomalySeverityLabel("LOW")}</option>
            </Form.Select>
          </div>

          <div className="filter-group anomaly-branch-filter-group">
            <label className="label-with-tip">
              {t("AnomalyDetection.childBranch.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.childBranchFilterToolTip.label")} />
            </label>
            <Form.Select
              value={branchId ? String(branchId) : ""}
              onChange={handleBranchFilterChange}
              disabled={isLoadingBranches}
              aria-label={t("AnomalyDetection.childBranch.label")}
            >
              <option value="">{t("AnomalyDetection.allBranches.label")}</option>
              {childBranches.map((unit) => (
                <option key={unit.id} value={String(unit.id)}>
                  {formatChildBranchOptionLabel(unit)}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Rule Type multi-select */}
          <div className="filter-group" ref={ruleDropRef}>
            <label className="label-with-tip">
              {t("AnomalyDetection.ruleType.label")}
              <AnomalyInfoTip text={t("AnomalyDetection.ruleTypeToolTip.label")} />
            </label>
            <div className="rule-multiselect">
              <button
                type="button"
                className={`rule-multiselect-trigger${ruleDropOpen ? " open" : ""}`}
                onClick={() => setRuleDropOpen((prev) => !prev)}
              >
                <span className="rule-trigger-text">
                  {ruleCodes.length === 0
                    ? t("AnomalyDetection.allRules.label")
                    : t("AnomalyDetection.rulesSelected.label", { count: ruleCodes.length })}
                </span>
                <span className="rule-trigger-arrow">{ruleDropOpen ? "▲" : "▼"}</span>
              </button>
              {ruleDropOpen && (
                <div className="rule-multiselect-dropdown">
                  {ANOMALY_RULE_CODES.map((opt) => (
                    <label key={opt.value} className="rule-option">
                      <input
                        type="checkbox"
                        checked={ruleCodes.includes(opt.value)}
                        onChange={(e) =>
                          setRuleCodes(
                            e.target.checked
                              ? [...ruleCodes, opt.value]
                              : ruleCodes.filter((c) => c !== opt.value)
                          )
                        }
                      />
                      <span>{resolveAnomalyRuleLabel(t, opt.value)}</span>
                    </label>
                  ))}
                  {ruleCodes.length > 0 && (
                    <button
                      type="button"
                      className="rule-clear-btn"
                      onClick={() => { setRuleCodes([]); setRuleDropOpen(false); }}
                    >
                      {t("AnomalyDetection.clearRules.label")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label>
              {isHistoryView
                ? t("AnomalyDetection.resolvedDate.label")
                : t("AnomalyDetection.period.label")}
            </label>
            {isHistoryView ? (
              <div className="period-picker-wrapper">
                <DatePicker
                  selected={periodFrom}
                  onChange={onAnomalyResolvedPeriodChange(setPeriodFrom)}
                  maxDate={getAnomalyResolvedPeriodFromMaxDate(periodTo)}
                  dateFormat={ANOMALY_DATEPICKER_FORMAT}
                  placeholderText={t("MetricAnomalyTimeline.periodFromPlaceholder.label")}
                />
                <span className="period-sep">→</span>
                <DatePicker
                  selected={periodTo}
                  onChange={onAnomalyResolvedPeriodChange(setPeriodTo)}
                  minDate={periodFrom || undefined}
                  maxDate={periodMaxDate}
                  dateFormat={ANOMALY_DATEPICKER_FORMAT}
                  placeholderText={t("MetricAnomalyTimeline.periodToPlaceholder.label")}
                />
              </div>
            ) : (
              <AnomalyPeriodSelector
                onChange={(from, to) => {
                  setPeriodFrom(from);
                  setPeriodTo(to);
                }}
              />
            )}
          </div>

          <Button variant="primary" size="sm" onClick={handleSearch}>
            {t("Apply.label")}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleReset}>
            {t("Reset.label")}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="List_Table_01">
        <div className="card List_table-card01">
          {isLoading ? (
            <div className="table-loading-wrap">
              <ScaleLoader color="#3b82f6" />
            </div>
          ) : (
            <Table responsive="sm" hover className={`anomaly-list-table${isHistoryView ? " anomaly-history-table" : ""}`}>
              <thead>
                <tr>
                  <th className="anomaly-col-period">
                    <span className="label-with-tip">
                      {t("AnomalyDetection.period.label")}
                      <AnomalyInfoTip text={t("AnomalyDetection.periodColumnToolTip.label")} />
                    </span>
                  </th>
                  <th className="anomaly-col-metric">{t("AnomalyDetection.metric.label")}</th>
                  <th className="anomaly-col-rule">{t("AnomalyDetection.ruleType.label")}</th>
                  <th className="anomaly-col-severity">{t("AnomalyDetection.severity.label")}</th>
                  {!isHistoryView && (
                    <th className="anomaly-col-status">{t("AnomalyDetection.status.label")}</th>
                  )}
                  <th className="anomaly-col-deviation">
                    <span className="label-with-tip">
                      {t("AnomalyDetection.deviationPct.label")}
                      <AnomalyInfoTip text={t("AnomalyDetection.deviationPctToolTip.label")} />
                    </span>
                  </th>
                  {!isHistoryView && (
                    <th className="anomaly-col-first-detected">{t("AnomalyDetection.firstDetected.label")}</th>
                  )}
                  {isHistoryView && (
                    <>
                      <th className="anomaly-col-resolved-at">{t("AnomalyDetection.resolvedAt.label")}</th>
                      <th className="anomaly-col-resolution">{t("AnomalyDetection.resolutionSummary.label")}</th>
                    </>
                  )}
                  <th className="anomaly-col-actions">{t("AnomalyDetection.actions.label")}</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan}>
                      <div className="Nodatafound_box text-center py-4">
                        <FcFinePrint size={40} />
                        <p className="mt-2 text-muted">{t("Nodatafound.label")}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  list.map((item) => (
                    <tr key={item.id}>
                      <td className="anomaly-col-period">
                        {formatAnomalyRecordDisplayPeriod(item)}
                      </td>
                      <td className="anomaly-col-metric">
                        <div className="metric-cell">
                          <span
                            className="metric-link metric-primary"
                            title={t("AnomalyDetection.viewTimeSeries.label")}
                            onClick={() =>
                              navigate("../time_series", {
                                state: getTimeSeriesNavState({
                                  metricId: item.metricId,
                                  metricName: item.metricName,
                                  selectedPeriod: getAnomalyDisplayPeriod(item) || item.period,
                                }),
                              })
                            }
                          >
                            {item.metricName || "—"}
                          </span>
                          {item.datapointName && (
                            <span
                              className="datapoint-secondary"
                              title={t("AnomalyDetection.viewTimeSeries.label")}
                              onClick={() =>
                                navigate("../time_series", {
                                  state: getTimeSeriesNavState({
                                    metricId: item.metricId,
                                    metricName: item.metricName,
                                    selectedPeriod: getAnomalyDisplayPeriod(item) || item.period,
                                    datapointId: item.datapointId,
                                    datapointName: item.datapointName,
                                  }),
                                })
                              }
                            >
                              {item.datapointName}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="anomaly-col-rule">
                        {item.ruleCode ? (
                          <span className="rule-code-label">
                            {resolveAnomalyRuleLabel(t, item.ruleCode)}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="anomaly-col-severity">{renderSeverityBadge(item.severity)}</td>
                      {!isHistoryView && (
                        <td className="anomaly-col-status">{renderStatusBadge(item.status)}</td>
                      )}
                      <td className="anomaly-col-deviation">{renderDeviationPct(item.deviationPct)}</td>
                      {!isHistoryView && (
                        <td className="anomaly-col-first-detected">
                          {item.firstDetectedAt
                            ? formatAnomalyDisplayDate(item.firstDetectedAt)
                            : "—"}
                        </td>
                      )}
                      {isHistoryView && (
                        <>
                          <td className="anomaly-col-resolved-at history-resolved-at-cell">
                            {item.resolvedAt
                              ? formatAnomalyDisplayDateTime(item.resolvedAt)
                              : "—"}
                          </td>
                          <td className="anomaly-col-resolution history-resolution-cell">
                            {renderResolutionSummary(item)}
                          </td>
                        </>
                      )}
                      <td className="anomaly-col-actions">
                        <div className="action-btns d-flex align-items-center justify-content-end gap-2">
                          <Dropdown align="end">
                            <Dropdown.Toggle variant="blank_outline" size="sm" className="border-0 px-2  ">
                              <BsThreeDotsVertical />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {!isHistoryView && item.status === "OPEN" && canShowFixAction(item) && (
                                <Dropdown.Item onClick={() => openFix(item)} className="text-primary fw-bold">
                                  {t("AnomalyDetection.fix.label")}
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item onClick={() => openDetail(item)}>
                                {t("AnomalyDetection.view.label")}
                              </Dropdown.Item>
                              {!isHistoryView && item.status === "OPEN" && (
                                <Dropdown.Item onClick={() => openDismiss(item)} className="text-warning">
                                  {t("AnomalyDetection.dismiss.label")}
                                </Dropdown.Item>
                              )}
                              {!isHistoryView && item.status === "DISMISSED" && (
                                <Dropdown.Item onClick={() => handleRevoke(item.id)}>
                                  {t("AnomalyDetection.reopen.label")}
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
          {/* Pagination */}
          {totalCount > 0 && (
            <div className="List_footer_count d-flex align-items-center mt-2">
              <Pagination
                itemsTotalCount={totalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                setItemsPerPage={setItemsPerPage}
                alwaysShown={false}
                pageClicked={(page, perPage) => {
                  const nextPage = page || 1;
                  const nextSize = perPage || itemsPerPage;
                  if (perPage) setItemsPerPage(perPage);
                  setCurrentPage(nextPage);
                  fetchList(nextPage, {}, { pageSize: nextSize });
                }}
              />
            </div>
          )}
        </div>
      </div>

 

      {/* Dismiss Modal */}
      <Modal show={showDismiss} onHide={() => setShowDismiss(false)} centered className="anomaly-glass-modal">
        <Modal.Header closeButton>
          <Modal.Title as="h6">{t("AnomalyDetection.dismissModal.label")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="label-with-tip">
                {t("AnomalyDetection.dismissalType.label")}
                <AnomalyInfoTip text={t("AnomalyDetection.dismissalTypeToolTip.label")} />
              </Form.Label>
              <Form.Select
                value={dismissType}
                onChange={(e) => setDismissType(e.target.value)}
              >
                {DISMISS_TYPES.map((dt) => (
                  <option key={dt} value={dt}>{formatDismissTypeLabel(dt)}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {dismissType === "UNTIL_DATE" && (
              <Form.Group className="mb-3">
                <Form.Label className="label-with-tip">
                  {t("AnomalyDetection.suppressUntilDate.label")}
                  <AnomalyInfoTip text={t("AnomalyDetection.suppressUntilDateToolTip.label")} />
                </Form.Label>
                <div>
                  <DatePicker
                    selected={suppressDate}
                    onChange={setSuppressDate}
                    dateFormat={ANOMALY_DATEPICKER_FORMAT}
                    minDate={new Date()}
                    placeholderText={t("MetricAnomalyTimeline.periodFromPlaceholder.label")}
                    className="form-control"
                  />
                </div>
              </Form.Group>
            )}

            {dismissType === "UNTIL_VALUE_CHANGES" && (
              <Form.Group className="mb-3">
                <Form.Label className="label-with-tip">
                  {t("AnomalyDetection.suppressUntilValue.label")}
                  <AnomalyInfoTip text={t("AnomalyDetection.suppressUntilValueToolTip.label")} />
                </Form.Label>
                <Form.Control
                  value={suppressValue}
                  onChange={(e) => setSuppressValue(e.target.value)}
                  placeholder={t("AnomalyDetection.suppressUntilValue.label")}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-1">
              <Form.Label className="label-with-tip">
                {t("AnomalyDetection.reason.label")}
                <AnomalyInfoTip text={t("AnomalyDetection.dismissReasonToolTip.label")} />
              </Form.Label>
              <div className="dismiss-quick-reasons mb-2">
                <div className="text-muted small mb-1">Suggestions:</div>
                {getDismissalSuggestions(dismissRecord?.ruleCode).map((reason) => (
                  <Button
                    key={reason}
                    variant="outline-secondary"
                    size="sm"
                    className="me-2 mb-1"
                    onClick={() => setDismissReason(reason)}
                  >
                    {reason}
                  </Button>
                ))}
              </div>
              <Form.Control
                as="textarea"
                rows={3}
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                placeholder={t("AnomalyDetection.reason.label")}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDismiss(false)}>
            {t("Cancel.label")}
          </Button>
          <Button
            variant="primary"
            className="sbmBtn"
            onClick={handleDismissSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <ScaleLoader color="#fff" height={14} width={2} />
            ) : (
              t("AnomalyDetection.dismiss.label")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <AnomalyDetailModal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        loading={loadingDetail}
        record={detailRecord}
        previewRecord={previewRecord}
        onViewTimeSeries={navigateToTimeSeriesFromRecord}
        onFix={openFix}
      />

      <AnomalyFixModal
        show={showFix}
        recordId={fixRecordId}
        onHide={() => {
          setShowFix(false);
          setFixRecordId(null);
        }}
        onSuccess={handleFixSuccess}
        onViewTimeSeries={navigateToTimeSeriesFromRecord}
      />
    </div>
  );
};

export default withTranslation()(AnomalyList);
